import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, signal, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, of, Subscription, switchMap } from 'rxjs';
import { EventBusService } from 'shared/services/event-bus.service';
import { PermissionsService } from 'shared/services/permissions.service';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { AccessGroupsTableComponent } from '../access-groups/access-groups-table/access-groups-table.component';
import { InvitedUsersTableComponent } from '../invited-users/invited-users-table/invited-users-table.component';
import { ShijiUsersTableComponent } from '../shiji-users/shiji-users-table/shiji-users-table.component';
import { SupplierUsersTableComponent } from '../supplier-users/supplier-users-table/supplier-users-table.component';
import { UserGroupsTableComponent } from '../user-groups/user-groups-table/user-groups-table.component';

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    standalone: false,
})
export class UsersComponent implements AfterViewInit, OnDestroy {
    @ViewChild('usersMainView', { read: ViewContainerRef }) usersMainView!: ViewContainerRef;

    public mainButtonLabel = signal<string | undefined>(undefined);

    private subscriptions: { [key: string]: Subscription } = {};
    private activeUrlKey = '';
    private tabLinkMapping: {
        [key: string]: Type<
            | SupplierUsersTableComponent
            | InvitedUsersTableComponent
            | UserGroupsTableComponent
            | AccessGroupsTableComponent
            | ShijiUsersTableComponent
        >;
    } = {
        users: SupplierUsersTableComponent,
        invitedUsers: InvitedUsersTableComponent,
        userGroups: UserGroupsTableComponent,
        accessGroups: AccessGroupsTableComponent,
        shijiUsers: ShijiUsersTableComponent,
    };

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private eventBusService: EventBusService,
        private permissionsService: PermissionsService,
        private cdr: ChangeDetectorRef,
    ) {
        const combinedAccessResponse = this.permissionsService.userHasAccessToAll([
            'users.tabs.users',
            'users.tabs.invitedUsers',
            'users.tabs.userGroups',
            'users.tabs.accessGroups',
            'users.tabs.shijiUsers',
        ]);

        this.activatedRoute.url
            .pipe(
                switchMap((urlSegments: any) => this.permissionsService.userHasAccess(`users.tabs.${urlSegments[0]?.path}`)),
                switchMap((hasAccess) => combineLatest([of(hasAccess), combinedAccessResponse])),
            )
            .subscribe(([hasAccessToCurrentUrl, combinedAccessResponse]) => {
                if (!hasAccessToCurrentUrl) {
                    //We go to the first available tab
                    const linkKey =
                        Object.keys(this.tabLinkMapping).find(
                            (key, index) => index === combinedAccessResponse.findIndex((elem) => Boolean(elem)),
                        ) ?? Object.keys(this.tabLinkMapping)[0];
                    this.router.navigate([`/users/${linkKey}`]);
                }
            });
    }

    public ngAfterViewInit(): void {
        this.subscriptions.route = this.activatedRoute.url.subscribe((urlSegments) => {
            this.activeUrlKey = urlSegments[0]?.path;
            this.usersMainView.clear();
            this.usersMainView.createComponent(this.tabLinkMapping[this.activeUrlKey]);
            this.mainButtonLabel.set(`users.table.create.${this.activeUrlKey}`);
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public pageMainButtonAction() {
        this.eventBusService.dispatch('pageMainButtonAction', this.activeUrlKey);
    }
}
